import type {NetworkDisplayEntry} from "@/network/model/NetworkDisplayEntry";
import DOMPurify from "dompurify";
import {notSet} from "@/utils/ValueUtils";
import {isIterable} from "@/utils/ObjectHelpers";

export function addEventListenerToAll(
    selectors: string,
    eventTypeOrTypes: string | string[],
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions
): void {
    const types = typeof eventTypeOrTypes === "string"
        ? [eventTypeOrTypes]
        : eventTypeOrTypes;
    for (const el of document.querySelectorAll(selectors)) {
        for (const type of types) {
            el.addEventListener(type, listener, options);
        }
    }
}

export function addCssScopedEventListener(
    targetEl: Element | undefined | null,
    eventTypeOrTypes: string | string[],
    cssSelector: string | undefined | null,
    listener: EventListenerOrEventListenerObject | undefined | null,
    options?: boolean | AddEventListenerOptions,
): void {
    if (!targetEl || !eventTypeOrTypes || !cssSelector || !listener) {
        return;
    }
    const types = typeof eventTypeOrTypes === "string"
        ? [eventTypeOrTypes]
        : eventTypeOrTypes;
    const eventHandler = (e: Event) => {
        const elements = targetEl.querySelectorAll(cssSelector);
        for (const element of elements) {
            if (element === e.target || element.contains(e.target as Node)) {
                if (typeof listener === 'function') {
                    listener(e);
                } else if (typeof listener.handleEvent === 'function') {
                    listener.handleEvent(e);
                }
                break;
            }
        }
    };
    for (const type of types) {
        targetEl.addEventListener(type, eventHandler, options);
    }
}

/**
 * Loads the template and returns it as an element.
 *
 * Makes up for no simple way in the DOM API to load HTML directly.
 * e.g. we cannot do this:
 * `document.createElement(template)`
 *
 * @param template HTML template string to create a DOM element from (if null or
 *                 undefined, an empty string will be used instead).
 * @returns The created HTML element
 */
export function loadTemplate(template: string): HTMLElement {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = (template || '').trim();
    return tempDiv.firstChild as HTMLElement;
}

export function removeAllChildren(el: HTMLElement | undefined | null) {
    // "newer" function available since ~2022 (should be available in all modern browsers now)
    if (typeof el?.replaceChildren === 'function') {
        el.replaceChildren();
    } else {
        // Fall back to an older, and likely less efficient approach
        while (el?.firstElementChild) {
            el.firstElementChild.remove();
        }
    }
}

export function suppressEvent(
    e: Event,
    suppressImmediatePropagation: boolean = false,
): void {
    e.preventDefault();
    if (suppressImmediatePropagation) {
        e.stopImmediatePropagation();
    } else {
        e.stopPropagation();
    }
}

/**
 * Suppresses form submit events for either the provided form element
 * or any form elements that are children of the provided element.
 *
 * @param el The element (or parent element) to suppress submit events for.
 * You can also pass a JQuery object as well or an iterable of elements.
 */
export function suppressFormSubmit(el: unknown): void {
    if (notSet(el)) {
        return;
    }

    // Process an iterable object for each of it's entries
    if (isIterable(el) && !(el instanceof Element)) {
        // @ts-expect-error We used isIterable() to validate the type
        for (const entry of (el as Iterable)) {
            suppressFormSubmit(entry);
        }
        return;
    }

    // Handle this element
    if ((el instanceof Element && el.localName === "form") || el instanceof HTMLFormElement) {
        el.addEventListener("submit", suppressEvent);
    }

    // Handle any children of this element
    if (el instanceof Element) {
        for (const formEl of el.querySelectorAll<HTMLFormElement>("form")) {
            formEl.addEventListener("submit", suppressEvent);
        }
    }
}

export function toElement(elementOrElementId?: HTMLElement | string): HTMLElement | null {
    if (notSet(elementOrElementId)) {
        return null;
    }
    return elementOrElementId instanceof HTMLElement
        ? elementOrElementId
        : document.getElementById(elementOrElementId);
}

export function setSelectedOptions(el: HTMLSelectElement, options: string[]) {
    if (!el) {
        return;
    }
    for (const optionEl of el.options) {
        optionEl.selected = options?.includes(optionEl.value) ?? false;
    }
}

export function populateSelectElement(
    el: HTMLSelectElement,
    options: Array<NetworkDisplayEntry>
) {
    if (!el) {
        return;
    }
    removeAllChildren(el);
    if (!options) {
        return;
    }
    for (const option of options) {
        el.add(new Option(option.name, option.id.toString()));
    }
}

export function populateTagsSelectElement(
    el: HTMLSelectElement | null | undefined,
    selectedTags: string[] | null | undefined,
    alwaysIncludeTags: string[] | null | undefined,
) {
    if (!el) {
        return;
    }
    removeAllChildren(el);
    if (selectedTags) {
        for (const tag of selectedTags) {
            el.add(new Option(tag, tag, false, true));
        }
    }
    if (alwaysIncludeTags) {
        for (const tag of alwaysIncludeTags) {
            if (!selectedTags?.includes(tag)) {
                el.add(new Option(tag, tag));
            }
        }
    }
}

// FUTURE: This isn't actually manipulating the DOM, so probably should be in another file / "package"
export function sanitizeHtml(htmlString: string) :string {
    return DOMPurify.sanitize(
        htmlString,
        { USE_PROFILES: { html: true } }
    );
}

/**
 * This mimics the original behaviour of the jQuery extension function "disable"
 * that does more than it really should when disabling an element (like
 * modifying the classes). This is really intended as a migration helper only.
 *
 * Note that this function only acts on form field elements.
 *
 * @param el The element to "disable".
 * @param applyToChildren Indicates if children should also be acted upon.
 */
export function legacyDisableElement(el: Element, applyToChildren: boolean = true) {
    if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLButtonElement
    ) {
        el.disabled = true;
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            el.readOnly = true;
        }
        el.classList.add("readOnlyClass");
    }
    if (applyToChildren) {
        for (const childEl of el.children) {
            legacyDisableElement(childEl, true);
        }
    }
}

/**
 * This mimics the original behaviour of the jQuery extension function "enable"
 * that does more than it really should when enabling an element (like
 * modifying the classes). This is really intended as a migration helper only.
 *
 * Note that this function only acts on form field elements.
 *
 * @param el The element to "enable".
 * @param applyToChildren Indicates if children should also be acted upon.
 */
// noinspection JSUnusedGlobalSymbols
export function legacyEnableElement(el: Element, applyToChildren: boolean = true) {
    if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLButtonElement
    ) {
        el.disabled = false;
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            el.readOnly = false;
        }
        el.classList.remove("readOnlyClass");
    }
    if (applyToChildren) {
        for (const childEl of el.children) {
            legacyEnableElement(childEl, true);
        }
    }
}

/**
 * This mimics the original behaviour of the jQuery extension function "toggle"
 * that does more than it really should when toggling an element (like
 * modifying the classes). This is really intended as a migration helper only.
 *
 * Note that this function only acts on form field elements.
 *
 * @param el The element to "toggle".
 * @param enabled Whether to enable or disable the element.
 * @param applyToChildren Indicates if children should also be acted upon.
 */
export function legacyToggleElement(
    el: Element,
    enabled: boolean,
    applyToChildren: boolean = true,
): void {
    if (enabled) { // NOSONAR
        legacyEnableElement(el, applyToChildren);
    } else {
        legacyDisableElement(el, applyToChildren);
    }
}

type ElementCtor<T extends HTMLElement> = new (...args: unknown[]) => T;

/**
 * Returns a required DOM element by id as `HTMLElement`.
 *
 * @param id - The id of the element to retrieve, without the leading `#`.
 * @returns The matching DOM element as `HTMLElement`.
 * @throws {TypeError} If no element exists with the given id.
 *
 * @example
 * ```TypeScript
 * const basicEl = requiredElement("basic");
 * ```
 */
export function requiredElement(id: string): HTMLElement;

/**
 * Returns a required DOM element by id and verifies that it matches `ctor`.
 *
 * @typeParam T - The expected HTMLElement subtype.
 * @param id - The id of the element to retrieve, without the leading `#`.
 * @param ctor - The constructor for the expected element type, such as
 * `HTMLInputElement` or `HTMLSelectElement`.
 * @returns The matching DOM element, narrowed to `T`.
 * @throws {TypeError} If no element exists with the given id, or if the element
 * is not an instance of `ctor`.
 *
 * @example
 * ```TypeScript
 * const claimIdEl = requiredElement("claimId", HTMLInputElement);
 * const vendorIdEl = requiredElement("vendorId", HTMLSelectElement);
 * ```
 */
export function requiredElement<T extends HTMLElement>(
    id: string,
    ctor: ElementCtor<T>
): T;

/**
 * Returns a required DOM element by id and verifies its runtime type.
 *
 * This is the implementation signature for the `requiredElement` overloads.
 *
 * This helper is useful for replacing unsafe casts such as
 * `document.getElementById("someId") as HTMLInputElement` with a runtime-checked
 * lookup.
 *
 * @typeParam T - The expected HTMLElement subtype when `ctor` is provided.
 * @param id - The id of the element to retrieve, without the leading `#`.
 * @param ctor - Optional constructor for the expected element type, such as
 * `HTMLInputElement` or `HTMLSelectElement`. Defaults to `HTMLElement` when omitted.
 * @returns The matching DOM element.
 * @throws {TypeError} If no element exists with the given id, or if the element
 * is not an instance of the expected constructor.
 *
 * @example
 * ```TypeScript
 * const basicEl = requiredElement("basic");
 * const claimIdEl = requiredElement("claimId", HTMLInputElement);
 * const vendorIdEl = requiredElement("vendorId", HTMLSelectElement);
 * ```
 */
export function requiredElement<T extends HTMLElement>(
    id: string,
    ctor?: ElementCtor<T>,
): HTMLElement | T {
    const element = document.getElementById(id);
    const expectedCtor = ctor ?? HTMLElement;

    if (!(element instanceof expectedCtor)) {
        throw new TypeError(`Expected #${id} to be ${expectedCtor.name}`);
    }

    return element;
}

/**
 * Returns a required DOM element by CSS selector and verifies that it is an
 * instance of the expected HTML element type.
 *
 * This helper is useful for replacing unsafe casts such as
 * `document.querySelector(".my-input") as HTMLInputElement` with a
 * runtime-checked lookup.
 *
 * @typeParam T - The expected HTMLElement subtype.
 * @param selector - The CSS selector used to locate the element.
 * @param ctor - The constructor for the expected element type, such as
 * `HTMLInputElement` or `HTMLSelectElement`.
 * @returns The matching DOM element, narrowed to the requested HTMLElement subtype.
 * @throws {TypeError} If no element matches the selector, or if the matched
 * element is not an instance of `ctor`.
 *
 * @example
 * ```TypeScript
 * const searchInput = requiredElementBySelector(".search-area input", HTMLInputElement);
 * const countrySelect = requiredElementBySelector(".country-area select", HTMLSelectElement);
 * ```
 */
export function requiredElementBySelector<T extends HTMLElement>(
    selector: string,
    ctor: new (...args: unknown[]) => T,
): T {
    const element = document.querySelector(selector);
    if (!(element instanceof ctor)) {
        throw new TypeError(`Expected ${selector} to be ${ctor.name}`);
    }
    return element;
}

/**
 * Returns an optional DOM element by id and verifies that, if present, it is an
 * instance of the expected HTML element type.
 *
 * @typeParam T - The expected HTMLElement subtype.
 * @param id - The id of the element to retrieve, without the leading `#`.
 * @param ctor - The constructor for the expected element type, such as
 * `HTMLInputElement` or `HTMLSelectElement`.
 * @returns The matching DOM element, narrowed to the requested HTMLElement subtype,
 * or `null` when no element exists.
 * @throws {TypeError} If the element exists but is not an instance of `ctor`.
 */
export function optionalElement<T extends HTMLElement>(
    id: string,
    ctor: new (...args: unknown[]) => T,
): T | null {
    const element = document.getElementById(id);
    if (!element) {
        return null;
    }
    if (!(element instanceof ctor)) {
        throw new TypeError(`Expected #${id} to be ${ctor.name}`);
    }
    return element;
}

export type HTMLInputValueElements = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
export type HTMLInputElements = HTMLInputValueElements | HTMLButtonElement;

/**
 * Queries the given HTMLElement for all input-related elements, including
 * input, select, textarea, and button elements.
 *
 * @param el The root HTMLElement within which to search for input-related elements.
 * @return A NodeListOf input-related elements (input, select, textarea, and button).
 */
export function queryInputElements(el: HTMLElement): NodeListOf<HTMLInputElements> {
    return el.querySelectorAll<HTMLInputElements>("input, select, textarea, button");
}

export function isInputElement(
    el: unknown,
): el is HTMLInputElements {
    return isInputValueElement(el) || el instanceof HTMLButtonElement;
}

export function isInputValueElement(
    el: unknown,
): el is HTMLInputValueElements {
    return el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement;
}
